import { useCallback, useContext, useEffect, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import useItem from 'common/hooks/useItem';
import useRouter from 'common/hooks/use-router';
import DetailHeader from 'common/components/DetailHeader/DetailHeader';
import HeaderButtons from 'common/components/DetailHeader/HeaderButtons';
import SectionTitle from 'common/components/SectionTitle/SectionTitle';
import PageLoading from 'common/components/PageLoading/PageLoading';
import Icon from 'components/Icon/Icon';
import AlertsContext from 'common/providers/alerts';
import { Operator } from 'types/Operator';
import Calendar from 'common/components/Calendar/Calendar';
import { Event } from 'types/Event';
import useAsync from 'common/hooks/useAsync';
import fetchJSON from 'common/utils/fetchJSON';
import useList from 'common/hooks/useList';
import {
  calculateDateRangeForView,
  getDateFromRange,
  areDateRangesEqual,
} from 'common/components/Calendar/calendarUtils';

function MediatorDetails({ userId }: { userId?: string }) {
  const router = useRouter();
  const id = userId ?? (router.query.id as string) ?? '';
  const { t } = useTranslation();
  const { setAlert } = useContext(AlertsContext);

  const {
    item: user,
    isFetching,
    error,
  }: any = useItem('users', id, {
    populate: ['operator.establishments', 'operator.establishments.department', 'role'],
    cachePrefix: 'mediator_availability',
  });

  useEffect(() => {
    if (error) {
      setAlert(error, 'danger');
    }
  }, [error, setAlert]);

  const editedOperator = user?.operator;

  const { username, firstname, lastname, establishments, updatedAt } =
    (editedOperator as Operator) || {};

  // État pour suivre la date actuelle du calendrier
  const [currentDate, setCurrentDate] = useState<Date>(new Date());

  // Default view of the calendar
  const [calendarDefaultView, setCalendarDefaultView] = useState<
    'month' | 'week' | 'day' | 'agenda'
  >('week');

  // Référence pour stocker l'état du calendrier
  const calendarStateRef = useRef({
    currentView: calendarDefaultView,
    currentDate: new Date(),
    lastLoadedRange: null as { start: Date; end: Date; view: string } | null,
  });

  // Utilisation de useAsync pour les disponibilités (availability)
  const fetchAvailabilitiesAsync = useCallback(
    async ({ start, end, operatorId }: { start: Date; end: Date; operatorId?: string }) => {
      try {
        const response = await fetchJSON({
          url: 'events/availability',
          method: 'POST',
          payload: { start, end, operatorId },
        });

        return response.data;
      } catch (error) {
        console.error('Erreur lors de la récupération des disponibilités:', error);
        // Retourner un tableau vide en cas d'erreur
        return [];
      }
    },
    []
  );

  const {
    data: availabilities,
    isProcessing: isAvailabilitiesFetching,
    execute: fetchAvailabilities,
  } = useAsync<Event[]>(fetchAvailabilitiesAsync, {
    cachePrefix: `mediator_availabilities_${id}`,
    ready: Boolean(id),
    storeData: true,
    manual: true,
  });

  // Chargement des événements réguliers (non-disponibilités)
  const {
    items: events,
    error: eventsError,
    isFetching: isEventsFetching,
    setFilters: setEventsFilters,
    fetchItems: fetchEvents,
  } = useList<Event>('events', {
    defaultSorts: ['start:ASC'],
    populate: ['mediatorOnEstablishment', 'mediatorOnEstablishment.department', 'operator'],
    ready: !!id,
    cachePrefix: 'mediator_regular_events',
    manual: true,
  });

  // Fonction pour charger les événements pour une plage de dates étendue
  const loadEventsForDateRange = useCallback(
    (date: Date, view: 'month' | 'week' | 'day' | 'agenda') => {
      if (!editedOperator || !id) return;

      // Vérifier si les données sont déjà en cours de chargement
      if (isEventsFetching || isAvailabilitiesFetching) return;

      // Calculer la plage optimisée en fonction de la vue
      const { start, end } = calculateDateRangeForView(date, view);

      // Stocker la plage de dates actuelle pour éviter des rechargements inutiles
      const currentRange = {
        start,
        end,
        view,
      };

      // Comparer avec la plage précédente pour éviter des rechargements inutiles
      const prevRange = calendarStateRef.current.lastLoadedRange;

      // Si la plage est la même, ne pas recharger
      if (prevRange && areDateRangesEqual(prevRange, currentRange)) {
        return;
      }

      // Mettre à jour la dernière plage chargée
      calendarStateRef.current.lastLoadedRange = currentRange;
      calendarStateRef.current.currentView = view;
      calendarStateRef.current.currentDate = date;

      // Configurer les filtres pour les événements réguliers
      const eventsFilters = {
        eventType: { $ne: 'availability' }, // Tous les événements sauf les disponibilités
        mediator: {
          id: { $eq: id },
        },
        $or: [
          {
            start: {
              $lte: end,
            },
          },
          {
            end: {
              $gte: start,
            },
          },
        ],
      };

      console.log(eventsFilters);

      // Mettre à jour les filtres pour les événements réguliers
      setEventsFilters(eventsFilters);
      //fetchEvents();

      // Charger les disponibilités
      fetchAvailabilities({
        start,
        end,
        operatorId: editedOperator?.id,
      });
    },
    [
      id,
      editedOperator?.id,
      fetchAvailabilities,
      fetchEvents,
      setEventsFilters,
      isEventsFetching,
      isAvailabilitiesFetching,
    ]
  );

  // Fonction pour gérer le changement de plage de dates
  const handleRangeChange = useCallback(
    (range: Date[] | { start: Date; end: Date }) => {
      // Pour la vue mois, on ignore les changements de plage car ils sont déjà gérés par handleNavigate
      if (calendarStateRef.current.currentView === 'month') {
        return;
      }

      // Extraire la date de référence de la plage
      const date = getDateFromRange(range);

      // Charger les événements pour la nouvelle plage de dates
      loadEventsForDateRange(date, calendarStateRef.current.currentView);
    },
    [loadEventsForDateRange]
  );

  // Fonction pour gérer la navigation dans le calendrier
  const handleNavigate = useCallback(
    (date: Date, view?: any) => {
      // Mettre à jour la date actuelle
      setCurrentDate(date);

      // Si la vue est fournie, la mettre à jour également
      const currentView = view || calendarStateRef.current.currentView;

      // Recharger les événements pour la nouvelle date
      loadEventsForDateRange(date, currentView);
    },
    [loadEventsForDateRange]
  );

  // Effet initial pour charger les événements au démarrage
  useEffect(() => {
    if (id) {
      // Charger les événements pour la plage étendue au démarrage, uniquement au montage initial
      if (!calendarStateRef.current.lastLoadedRange) {
        loadEventsForDateRange(new Date(), calendarDefaultView);
      }
    }
  }, [id, loadEventsForDateRange, calendarDefaultView]);

  const handleEdit = useCallback(() => {
    router.navigate(`/mediators/edit/${id}`);
  }, [id, router]);

  const MediatorHeaderInfos = useMemo(
    () => (
      <div className="w-full">
        <div className="flex items-center text-sm gap-2 field-title">
          <Icon name="ClockIcon" className="w-4 h-4" />
          {dayjs(updatedAt).fromNow()}
        </div>
        <div className="responsive-flex items-center gap-3">
          <div className="flex flex-row items-center text-ternary-800">
            <div className="flex flex-row items-center gap-2">
              <Icon name="EnvelopeIcon" className="w-4 h-4" />
              <span>{user?.email || t('forms.noEmail')}</span>
            </div>
          </div>
        </div>
      </div>
    ),
    [user?.email, t, updatedAt]
  );

  if (isFetching && !user) {
    return <PageLoading />;
  }

  return (
    <div className="mx-auto max-w-full py-6 px-6 text-slate-500 text-base">
      <DetailHeader
        backLink="/mediators"
        title={`${username}`}
        subtitleIcon="UserIcon"
        HeaderInfos={MediatorHeaderInfos}
        SubTitle={<span className="font-bold">{`${firstname} ${lastname}`}</span>}
        HeaderButtons={<HeaderButtons editUrl={`/mediators/edit/${id}`} />}
      />

      <div className="responsive-flex gap-2 mt-2">
        <div className="flex-1">
          <div className="flex flex-col gap-2 w-full">
            {/* Contenu principal */}
            <div className="responsive-flex flex flex-col 2xl:flex-row gap-2 items-start">
              {/* Colonne 1 - Calendrier (2/3) */}
              <div className="white-box responsive-flex gap-2 flex-col w-full 2xl:w-2/3">
                <SectionTitle
                  title={t('forms.availability')}
                  icon="CalendarDaysIcon"
                  className="text-fourth mt-2"
                />
                <div className="responsive-flex py-2 px-4 flex-col rounded">
                  <Calendar
                    events={events || []}
                    backgroundEvents={availabilities || []}
                    style={{ minHeight: '600px' }}
                    defaultView="week"
                    activeViews={{ month: false, week: true, day: false, agenda: false }}
                    onRangeChange={handleRangeChange}
                    onNavigate={handleNavigate}
                    date={currentDate}
                    draggable={false}
                    resizable={false}
                  />
                </div>
              </div>

              {/* Colonne 2 - Informations du médiateur (1/3) */}
              <div className="gap-4 flex flex-col w-full 2xl:w-1/3">
                {/* Établissements */}
                <div className="white-box flex flex-col flex-1 gap-2 mt-2">
                  <SectionTitle
                    title={t('forms.establishments')}
                    icon="EstablishmentIcon"
                    className="text-fourth mt-2"
                  />
                  <div className="responsive-flex py-2 px-4 flex-col rounded">
                    {establishments && establishments.length > 0 ? (
                      <div className="flex flex-col gap-2">
                        {establishments.map((establishment) => (
                          <div
                            key={establishment.id}
                            className="flex flex-row items-center border gap-2 px-2 py-1 rounded-sm"
                          >
                            <Icon name="EstablishmentIcon" className="w-4 h-4" />
                            <span>
                              {`${establishment?.name} (${String(
                                establishment?.department?.CP
                              ).padStart(2, '0')})`}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p>{t('forms.noEstablishments')}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MediatorDetails;
